/**
 * Event service — CRUD operations against the beta API.
 * Handles all record types (Unscheduled, Planned, Deferred, Scheduled).
 */
import { UnscheduledEvent } from '../types';
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';

const logger = createLogger('EventService');

/**
 * Options for filtering the events list server-side. Keeping the heavy work on
 * the API keeps payloads small as the table grows toward production volumes.
 */
export interface EventQueryOptions {
  /** 'active' (IN_WORK or PENDING) or an exact status such as 'COMPLETED'. */
  status?: 'active' | string;
  /** Restrict to a single record type (Unscheduled, Planned, Deferred, Scheduled). */
  recordType?: string;
  /** Earliest timeDeclaredZulu to include (YYYY-MM-DD), e.g. a 180-day window. */
  since?: string;
}

/**
 * Builds the query string for a list request. Always requests the lightweight
 * 'list' projection so narrative fields aren't shipped to table views.
 */
function buildEventsQuery(options: EventQueryOptions): string {
  const params = new URLSearchParams({ view: 'list' });
  if (options.status) params.set('status', options.status);
  if (options.recordType) params.set('recordType', options.recordType);
  if (options.since) params.set('since', options.since);
  return params.toString();
}

/**
 * Fetches a filtered, lightweight list of events from the API.
 * Defaults to active records only to keep the common path small.
 *
 * Returns `null` on request failure (network error / non-OK response) so callers
 * can distinguish "the request failed" from "the server returned zero events"
 * (`[]`). This lets pollers keep the last good data on a transient failure
 * instead of blanking the table.
 */
export async function fetchEvents(
  options: EventQueryOptions = { status: 'active' },
): Promise<UnscheduledEvent[] | null> {
  try {
    const query = buildEventsQuery(options);
    const response = await authedFetch(`/events?${query}`);
    if (!response || !response.ok) {
      if (response) logger.error('Failed to fetch events', { status: response.status });
      return null;
    }
    const data = await response.json();
    return data.events || [];
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('fetchEvents failed', { error: message });
    return null;
  }
}

/**
 * Fetches active events (IN_WORK or PENDING) for the homepages and sidebar.
 * Returns `null` on failure (see fetchEvents).
 */
export async function fetchActiveEvents(): Promise<UnscheduledEvent[] | null> {
  return fetchEvents({ status: 'active' });
}

/**
 * Fetches completed events since the given date (YYYY-MM-DD) for History/Completed.
 * Returns `null` on failure (see fetchEvents).
 */
export async function fetchCompletedEvents(since: string): Promise<UnscheduledEvent[] | null> {
  return fetchEvents({ status: 'COMPLETED', since });
}

/**
 * Fetches the full record (including narrative fields) for a single event.
 * Used when opening the View/Edit modal so heavy fields are loaded on demand.
 */
export async function fetchEventById(eventId: string): Promise<UnscheduledEvent | null> {
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}`);
    if (!response || !response.ok) {
      if (response)
        logger.error('Failed to fetch event by id', { status: response.status, eventId });
      return null;
    }
    return await response.json();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('fetchEventById failed', { error: message, eventId });
    return null;
  }
}

/**
 * Creates a new event.
 */
export async function createEvent(event: UnscheduledEvent): Promise<UnscheduledEvent | null> {
  try {
    const response = await authedFetch('/events', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify(event),
    });
    if (!response || !response.ok) {
      if (response) logger.error('Failed to create event', { status: response.status });
      return null;
    }
    return await response.json();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('createEvent failed', { error: message });
    return null;
  }
}

/**
 * Updates an existing event.
 */
export async function updateEvent(event: UnscheduledEvent): Promise<UnscheduledEvent | null> {
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(event.eventId)}`, {
      method: 'PUT',
      includeContentType: true,
      body: JSON.stringify(event),
    });
    if (!response || !response.ok) {
      if (response) logger.error('Failed to update event', { status: response.status });
      return null;
    }
    return await response.json();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('updateEvent failed', { error: message });
    return null;
  }
}
